import React from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import useDesktop from '@components/shared/useDesktop';
import { PageSlice } from '@contracts';
import AnerobicBacteriaIcon from '@images/vms-flora-bacteria.svg';
import YeastIcon from '@images/vms-flora-fungi.svg';
import LactobacilliIcon from '@images/vms-flora-lactobacilli.svg';
import MycoplasmaIcon from '@images/vms-flora-mycoplasmas.svg';
import MicrobiomeVideo from '@videos/Microbiome_Animation_Optimised.mp4';
import LazyLoadVideo from './LazyLoadVideo';
import VMSVaginalFloraItem from './VMSVaginalFloraItem';

interface Props {
  slice: PageSlice;
}

const iconData = [
  {
    key: 'components.vms-vaginal-flora.lactobacilli',
    icon: LactobacilliIcon
  },
  {
    key: 'components.vms-vaginal-flora.yeast-fungi',
    icon: YeastIcon
  },
  {
    key: 'components.vms-vaginal-flora.anaerobic-bacteria',
    icon: AnerobicBacteriaIcon
  },
  {
    key: 'components.vms-vaginal-flora.mycoplasma',
    icon: MycoplasmaIcon
  }
];

const VMSVaginalFlora: React.FC<Props> = () => {
  const { t } = useTranslation();
  const desktop = useDesktop(1024);

  if (desktop) {
    return (
      <Container>
        <Inner>
          <Title>{t('components.vms-vaginal-flora.title')}</Title>
          <ThreeColumn>
            <IconBoxWrap>
              {iconData.slice(0, 2).map((item) => (
                <VMSVaginalFloraItem item={item} align="right" key={`vaginal-flora-item-right-${item.key}`} />
              ))}
            </IconBoxWrap>
            <video autoPlay={true} loop={true} muted={true} playsInline={true} width="412px" height="412px">
              <source src={MicrobiomeVideo} type="video/mp4" />
            </video>
            <IconBoxWrap>
              {iconData.slice(2, 4).map((item) => (
                <VMSVaginalFloraItem item={item} align="left" key={`vaginal-flora-item-left-${item.key}`} />
              ))}
            </IconBoxWrap>
          </ThreeColumn>
        </Inner>
      </Container>
    );
  }

  return (
    <Container>
      <Inner>
        <Title>{t('components.vms-vaginal-flora.title')}</Title>
        <VideoWrap>
          <LazyLoadVideo>
            <video autoPlay={true} loop={true} muted={true} playsInline={true} width="250px" height="250px">
              <source src={MicrobiomeVideo} type="video/mp4" />
            </video>
          </LazyLoadVideo>
        </VideoWrap>
        <IconBoxWrap>
          {iconData.map((item) => (
            <VMSVaginalFloraItem item={item} align="center" key={`vaginal-flora-item-center-${item.key}`} />
          ))}
        </IconBoxWrap>
      </Inner>
    </Container>
  );
};

export default VMSVaginalFlora;

const Container = styled.div`
  padding: 120px 0 0;

  @media (max-width: 1024px) {
    padding: 60px 30px 30px;
  }
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    flex-direction: column;
  }
`;

const ThreeColumn = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 60px;
  margin-top: 90px;
`;

const VideoWrap = styled.div`
  padding: 0;
  @media (max-width: 1024px) {
    padding-bottom: 30px;
  }
`;

const IconBoxWrap = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  gap: 65px;
  max-width: 300px;

  @media (max-width: 1024px) {
    gap: 30px;
    margin: 0 auto;
    align-items: flex-start;
  }
`;

const Title = styled.h3`
  font-family: 'Domaine';
  font-style: normal;
  font-weight: normal;
  font-size: 30px;
  line-height: 120%;
  text-align: center;
  color: ${Colors.navyBlue};

  @media (max-width: 1024px) {
    font-size: 24px;
    margin-bottom: 30px;
  }
`;

export const Heading = styled.h3`
  font-family: BrownStd;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 1.5px;
  text-align: center;
  text-transform: uppercase;
  @media (max-width: 1024px) {
    text-align: left;
  }
`;
